/**
 * Utilitários para design responsivo
 * Fornece funções de escala para garantir consistência visual em diferentes telas
 */

import { Dimensions, PixelRatio, Platform } from 'react-native';

// Dimensões base (iPhone 8/SE - 375x667)
const BASE_WIDTH = 375;
const BASE_HEIGHT = 667;

// Obtém dimensões da tela
const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

// Fatores de escala
const widthScale = SCREEN_WIDTH / BASE_WIDTH;
const heightScale = SCREEN_HEIGHT / BASE_HEIGHT;

/**
 * Escala um valor proporcionalmente à largura da tela
 * Útil para: padding horizontal, larguras fixas, fontes
 * @param {number} size - Tamanho base (em dp)
 * @returns {number} Tamanho escalado
 */
export const scaleWidth = (size) => {
    const scaledSize = size * widthScale;
    // Limita a escala para não ficar muito grande
    if (widthScale > 1.3) return size * 1.3;
    if (widthScale < 0.8) return size * 0.8;
    return Math.round(PixelRatio.roundToNearestPixel(scaledSize));
};

/**
 * Escala um valor proporcionalmente à altura da tela
 * Útil para: padding vertical, alturas fixas, margens verticais
 * @param {number} size - Tamanho base (em dp)
 * @returns {number} Tamanho escalado
 */
export const scaleHeight = (size) => {
    const scaledSize = size * heightScale;
    if (heightScale > 1.3) return size * 1.3;
    if (heightScale < 0.8) return size * 0.8;
    return Math.round(PixelRatio.roundToNearestPixel(scaledSize));
};

/**
 * Escala moderada - suaviza a diferença entre tamanhos
 * Útil para: fontes, ícones (onde escala muito agressiva pode ser ruim)
 * @param {number} size - Tamanho base (em dp)
 * @param {number} factor - Fator de suavização (0 = sem escala, 1 = escala total)
 * @returns {number} Tamanho escalado moderadamente
 */
export const moderateScale = (size, factor = 0.5) => {
    const newSize = size + (scaleWidth(size) - size) * factor;
    return Math.round(PixelRatio.roundToNearestPixel(newSize));
};

/**
 * Normaliza fonte para densidade de pixels do dispositivo
 * @param {number} size - Tamanho base da fonte
 * @returns {number} Tamanho normalizado
 */
export const normalizeFont = (size) => {
    const scale = SCREEN_WIDTH / BASE_WIDTH;
    const newSize = size * scale;

    // iOS usa pontos, Android usa dp - comportamento pode variar
    if (Platform.OS === 'ios') {
        return Math.round(PixelRatio.roundToNearestPixel(newSize));
    }

    // Android: considerar densidade de pixel
    const pixelRatio = PixelRatio.get();
    if (pixelRatio >= 3) {
        return size; // Alta densidade - não precisa escalar muito
    } else if (pixelRatio >= 2) {
        return Math.round(newSize);
    }
    return Math.round(newSize);
};

/**
 * Verifica se é uma tela pequena (< 360dp de largura)
 */
export const isSmallScreen = SCREEN_WIDTH < 360;

/**
 * Verifica se é uma tela grande (tablet: >= 600dp de largura)
 */
export const isTablet = SCREEN_WIDTH >= 600;

/**
 * Dimensões atuais da tela
 */
export const screenDimensions = {
    width: SCREEN_WIDTH,
    height: SCREEN_HEIGHT,
    widthScale,
    heightScale,
};

/**
 * Constantes de espaçamento responsivo
 */
export const spacing = {
    xs: scaleWidth(4),
    sm: scaleWidth(8),
    md: scaleWidth(12),
    lg: scaleWidth(16),
    xl: scaleWidth(20),
    xxl: scaleWidth(24),
};

/**
 * Tamanhos de fonte responsivos
 */
export const fontSizes = {
    xs: moderateScale(10),
    sm: moderateScale(12),
    md: moderateScale(14),
    lg: moderateScale(16),
    xl: moderateScale(18),
    xxl: moderateScale(22),
    title: moderateScale(24),
    heading: moderateScale(28),
};

export default {
    scaleWidth,
    scaleHeight,
    moderateScale,
    normalizeFont,
    isSmallScreen,
    isTablet,
    screenDimensions,
    spacing,
    fontSizes,
};
